import './AppLayout.css';

import React, { type FC, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import * as mqtt_instance from '@src/Main/mqtt_instance';
import { VirtualKeyboard } from './VirtualKeyboard';
import { AppLayoutFooter } from './AppLayoutFooter';
import { isTouchScreenDevice } from '@src/Main/main';
import { useConfigInfo } from '@src/contexts';
import { RemoteAccessModal } from './RemoteAccessModal';

import { type RobotAppsService } from '@src/types';
import { controlCenter } from '@ventionco/mm-machine-logic-robot';
import { RemoteSupportOverlayWrapper } from '@ventionco/remote-support-components';
import { estopManager } from '@src/Main/estop';
const {
  view: {
    RobotReadyStateNotReadyModal,
    RobotSafetyStateModal,
  },
} = controlCenter;

const MACHINEMOTION_SOFTWARE_VERSION_MQTT_TOPIC =
  'services/mm-vention-control/version';
const PENDANT_SOFTWARE_VERSION_MQTT_TOPIC = 'devices/pendant/sw-version';

interface AppLayoutProps {
  isPendantAvailable: boolean
  robotAppsService: RobotAppsService
}

/**
 * Creates the layout for the app, which consists of a footer and a virtual keyboard overlay on top of the page.
 */
export const AppLayout: FC<AppLayoutProps> = ({ isPendantAvailable, robotAppsService }) => {
  const [machineMotionSoftwareVersion, setMachineMotionSoftwareVersion] = useState('unknown version number');
  const [pendantSoftwareVersion, setPendantSoftwareVersion] = useState('unknown version number');
  const configInfoContext = useConfigInfo();
  const hardwareVersion = configInfoContext.configInfo?.hwVersion !== undefined ? configInfoContext.configInfo?.hwVersion : '';

  useEffect(() => {
    const onNewMachineMotionSoftwareVersion = (_topic, newMachineMotionSoftwareVersion): void => { setMachineMotionSoftwareVersion(newMachineMotionSoftwareVersion); };
    const machineMotionSoftwareVersionMqttTopicRegex = new RegExp(MACHINEMOTION_SOFTWARE_VERSION_MQTT_TOPIC);

    const mqttGetClientCancellation = mqtt_instance.getClient((mqttClient) => {
      mqttClient.subscribe(
        MACHINEMOTION_SOFTWARE_VERSION_MQTT_TOPIC,
        machineMotionSoftwareVersionMqttTopicRegex,
        onNewMachineMotionSoftwareVersion,
      );
    });

    return () => {
      mqttGetClientCancellation();
      // Fire and forget
      mqtt_instance.getClient((mqttClient) => {
        mqttClient.unsubscribe(
          MACHINEMOTION_SOFTWARE_VERSION_MQTT_TOPIC,
          machineMotionSoftwareVersionMqttTopicRegex,
          onNewMachineMotionSoftwareVersion,
        );
      });
    };
  }, []);

  useEffect(() => {
    const onNewPendantSoftwareVersion = (_topic, newPendantSoftwareVersion): void => { setPendantSoftwareVersion(newPendantSoftwareVersion); };
    const pendantSoftwareVersionMqttTopicRegex = new RegExp(PENDANT_SOFTWARE_VERSION_MQTT_TOPIC);

    const mqttGetClientCancellation = mqtt_instance.getClient((mqttClient) => {
      mqttClient.subscribe(
        PENDANT_SOFTWARE_VERSION_MQTT_TOPIC,
        pendantSoftwareVersionMqttTopicRegex,
        onNewPendantSoftwareVersion,
      );
    });

    return () => {
      mqttGetClientCancellation();
      // Fire and forget
      mqtt_instance.getClient((mqttClient) => {
        mqttClient.unsubscribe(
          PENDANT_SOFTWARE_VERSION_MQTT_TOPIC,
          pendantSoftwareVersionMqttTopicRegex,
          onNewPendantSoftwareVersion,
        );
      });
    };
  }, []);

  const location = useLocation();
  useEffect(() => {
    estopManager.refreshEstopModal();
  }, [location]);

  return (
    <>
      <RemoteSupportOverlayWrapper />
      <div id="app-layout">
        <RobotSafetyStateModal
          send={robotAppsService.send}
          selectors={robotAppsService.selectors}
        />
        <RobotReadyStateNotReadyModal
          selectors={robotAppsService.selectors}
          title={'Your robot is not in a ready state'}
          message={'Enable robot to continue'}
          buttonText={'ENABLE ROBOT'}
        />
        <div id="app-layout-content">
          <Outlet />
        </div>
        <AppLayoutFooter
          hardwareVersion={hardwareVersion}
          machineMotionSoftwareVersion={machineMotionSoftwareVersion}
          isPendantAvailable={isPendantAvailable}
          pendantSoftwareVersion={pendantSoftwareVersion}
        />
      </div>
      { isTouchScreenDevice() ? <VirtualKeyboard /> : null }
      <RemoteAccessModal/>
    </>
  );
};
